import { ChangeDetectionStrategy, Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ConfigConstants, isDefined } from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    mergeMap,
    pairwise,
    startWith,
    switchMap,
    take,
    takeUntil,
    withLatestFrom,
} from 'rxjs/operators';
import { updateDiscrepancyLog } from '../../store/actions/functional-elements.actions';
import {
    getFormCheckpointByXmlIdFactory,
    getFormFilters,
    getFormFunctionalElementByIdFactory,
    getFormParticipantCopyReadonly,
    getFormRadioOptionsByXmlIdFactory,
    getFormSelectedParticipantCopyId,
    getFormTypeFilters,
} from '../../store/selectors/form.selector';
import { FormsState } from '../../store/state/pace-forms.state';
import { Checkpoint } from '../../types/checkpoint';
import { ContentId } from '../../types/content-block';
import { DiscrepancyLog, initialDiscrepancyLogState } from '../../types/discrepancy-log';
import { FormFilters } from '../../types/form-filter';
import { FormTypeFilters } from '../../types/form-type-filters';
import { FormsConstants } from '../../types/forms-constants';
import { ParticipantCopyId } from '../../types/participant-copy';
import { RADIO_OPTION_TYPE, RadioOption } from '../../types/radio-option';
import { isAstoriaFunctionalElementVisible } from '../../utilities/answer-utilities';

@Component({
    selector: 'pace-forms-discrepancy-log',
    templateUrl: './discrepancy-log.component.html',
    styleUrls: ['./discrepancy-log.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class DiscrepancyLogComponent implements OnInit, OnDestroy {
    @Input()
    public id: ContentId;

    @Input()
    public parentForm: FormArray | undefined;

    private readOnly$: Observable<boolean>;
    public discrepancyLog$: Observable<DiscrepancyLog>;
    public controllingCheckpoint$: Observable<Checkpoint | undefined>;
    public options$: Observable<RadioOption[] | undefined>;
    public selectedParticipant$: Observable<ParticipantCopyId>;

    public typeFilters$: Observable<FormTypeFilters>;
    public formFilters$: Observable<FormFilters>;
    public visible$: Observable<boolean>;

    public discrepancyLogGroup: FormGroup;
    public inputFieldControl: FormControl;
    public checkboxControl: FormControl;

    public faCheck = faCheck;

    private destroy$ = new Subject<void>();
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly formBuilder: FormBuilder,
        private readonly store: Store<FormsState>,
    ) {
        this.readOnly$ = store.select(getFormParticipantCopyReadonly);
        this.typeFilters$ = store.select(getFormTypeFilters);
        this.formFilters$ = store.select(getFormFilters);
        this.selectedParticipant$ = store.select(getFormSelectedParticipantCopyId);
    }

    public ngOnInit(): void {
        this.inputFieldControl = this.formBuilder.control('');
        this.checkboxControl = this.formBuilder.control(false);
        this.discrepancyLogGroup = this.formBuilder.group({
            inputField: this.inputFieldControl,
            checkbox: this.checkboxControl,
        });
        if (this.parentForm) {
            const parent = this.parentForm;
            Promise.resolve(undefined)
                .then(() => {
                    parent.push(this.discrepancyLogGroup);
                })
                .catch((error: Error) =>
                    this.log.error(
                        'DiscrepancyLogComponent: ngOnInit: Error in parentForm.push: ',
                        JSON.stringify(error),
                    ),
                );
        }

        this.discrepancyLog$ = this.store
            .select(getFormFunctionalElementByIdFactory<DiscrepancyLog>({ id: this.id }))
            .pipe(filter(isDefined));
        this.controllingCheckpoint$ = this.discrepancyLog$.pipe(
            mergeMap((discrepancyLog) =>
                discrepancyLog.controllingCheckpoint.length > 0
                    ? this.store
                          .select(getFormCheckpointByXmlIdFactory({ xmlId: discrepancyLog.controllingCheckpoint }))
                          .pipe(take(1))
                    : of(undefined),
            ),
        );

        this.options$ = this.controllingCheckpoint$.pipe(
            switchMap((checkpoint) =>
                typeof checkpoint !== 'undefined'
                    ? this.store.select(getFormRadioOptionsByXmlIdFactory({ xmlId: checkpoint.xmlId }))
                    : of(undefined),
            ),
        );

        this.visible$ = combineLatest([
            this.discrepancyLog$,
            this.controllingCheckpoint$,
            this.options$,
            this.typeFilters$,
            this.formFilters$,
            this.selectedParticipant$,
        ]).pipe(
            map(([discrepancyLog, checkpoint, options, typeFilters, formFilters, selectedParticipant]) => {
                // The master displays everything. In a participant copy, we display only content that was filled out in
                // the current session and not loaded from XML.
                let visible =
                    selectedParticipant === FormsConstants.MASTER_PARTICIPANT_ID ||
                    !discrepancyLog.contentLoadedFromXml;

                // In addition to the rule above, a controlling checkpoint can control the visibility by either the type
                // of the checkpoint and a selected matching filter, or by the chosen answer: In a participant copy, we
                // display only values answered with "yes".
                const visibleByFilter = isAstoriaFunctionalElementVisible(
                    discrepancyLog,
                    checkpoint?.elementType,
                    typeFilters,
                    formFilters,
                    checkpoint?.xmlId ? { [checkpoint.xmlId]: options } : {},
                );
                visible = visibleByFilter && visible;
                if (typeof checkpoint !== 'undefined' && typeof options !== 'undefined') {
                    const selectedInMaster = options.find((o) => o.selectedInMaster);
                    const yesInMaster =
                        typeof selectedInMaster !== 'undefined' &&
                        selectedInMaster.optionType === RADIO_OPTION_TYPE.YES &&
                        !selectedInMaster.contentLoadedFromXml;
                    visible = visible && (selectedParticipant === FormsConstants.MASTER_PARTICIPANT_ID || yesInMaster);
                }

                return visible;
            }),
        );

        combineLatest([this.readOnly$, this.discrepancyLog$])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([readOnly, discrepancyLog]) => {
                this.inputFieldControl[
                    discrepancyLog.disabled || readOnly || discrepancyLog.value === FormsConstants.NA
                        ? 'disable'
                        : 'enable'
                ]({
                    emitEvent: false,
                });
                if (discrepancyLog.controllingCheckpoint.length === 0) {
                    this.checkboxControl[discrepancyLog.disabled || readOnly ? 'disable' : 'enable']({
                        emitEvent: false,
                    });
                }
            });

        this.discrepancyLog$
            .pipe(startWith(initialDiscrepancyLogState), pairwise(), takeUntil(this.destroy$))
            .subscribe(([oldDiscrepancyLog, discrepancyLog]) => {
                if (oldDiscrepancyLog.value !== discrepancyLog.value) {
                    this.inputFieldControl.patchValue(discrepancyLog.value);
                    if (discrepancyLog.controllingCheckpoint.length === 0) {
                        this.checkboxControl.patchValue(discrepancyLog.value === FormsConstants.NA, {
                            emitEvent: false,
                        });
                    }
                }
            });

        this.inputFieldControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                // we use take(1) because we are only interested in static properties of discrepancyLog$
                withLatestFrom(this.discrepancyLog$, this.readOnly$),
                takeUntil(this.destroy$),
            )
            .subscribe(([newValue, discrepancyLog, readOnly]: [string, DiscrepancyLog, boolean]) => {
                // if the control is disabled, don't do anything. It can still happen that the value is changed when
                // a different participant copy is loaded.
                if (readOnly) {
                    return;
                }
                this.store.dispatch(
                    updateDiscrepancyLog({
                        xmlId: discrepancyLog.xmlId,
                        id: discrepancyLog.id,
                        newValue,
                    }),
                );
            });

        this.checkboxControl.valueChanges
            .pipe(withLatestFrom(this.discrepancyLog$, this.readOnly$), takeUntil(this.destroy$))
            .subscribe(([value, discrepancyLog, readOnly]) => {
                // if the control is disabled, don't do anything. It can still happen that the value is changed when
                // a different participant copy is loaded.
                if (readOnly) {
                    return;
                }

                // if the discrepancy was not set to N.A. and the checkbox is now checked, we patch the control value
                // to N.A., which will trigger the valueChanges observable and dispatch the value to the state
                if (discrepancyLog.value !== FormsConstants.NA && value) {
                    this.inputFieldControl.patchValue(FormsConstants.NA);
                } else if (discrepancyLog.value === FormsConstants.NA && !value) {
                    // if the discrepancy was set to N.A. and the checkbox is now unchecked, we patch the control value
                    // to '', which will trigger the valueChanges observable and dispatch the value to the state
                    this.inputFieldControl.patchValue('');
                }
                this.inputFieldControl[value ? 'disable' : 'enable']({ emitEvent: false });
            });
    }

    public ngOnDestroy(): void {
        // remove form controls from parent
        if (this.parentForm) {
            const index = this.parentForm.controls.findIndex((control) => control === this.discrepancyLogGroup);
            this.parentForm.removeAt(index);
        }

        this.destroy$.next();
    }
}
